import {
  advancedFormControl,
  FormControlTypeEnum,
} from '@magieno/common';
import { IsBoolean, IsInt, IsNotEmpty, IsString, ValidateNested } from '@pristine-ts/class-validator';
import { FieldTypeEnum } from '../../../../../enums/field-type.enum';
import { v4 as uuid } from 'uuid';
import { translationBaseKey } from '../../../../../decorators/translation-base-key.decorator';
import { ErrorMessageBuilder } from '../../../../../builders/error-message.builder';
import { type } from '@pristine-ts/data-mapping-common';
import { CalculationDate } from '../../../../../models/calculation.date';

@translationBaseKey('income-tax-personal-spouse-common-options')
export class IncomeTaxPersonalSpouseCommonOptions {
  @advancedFormControl({
    displayableElements: {},
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  id: string = uuid();

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Prénom',
        en: 'First name',
      },
      tooltip: {
        fr: '',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  firstName: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Nom de famille',
        en: 'Last name',
      },
      tooltip: {
        fr: '',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  lastName: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Date de naissance',
        en: 'Birthdate',
      },
      tooltip: {
        fr: 'Indiquez la date de naissance. La date de naissance est utilisée pour déterminer l\'admissibilité à certains crédits d\'impôt.',
        en: 'Specify the birthdate. The birthdate is used to determine eligibility for certain tax credits.',
      },
    },
    fieldType: FieldTypeEnum.CalculationDate,
    type: FormControlTypeEnum.Input,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateNested()
  @type(() => new CalculationDate())
  birthdate: CalculationDate;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Montant de revenu d\'emploi',
        en: 'Employment income',
      },
      tooltip: {
        fr: 'Indiquez le montant du revenu d\'emploi de votre conjoint. Le revenu d\'emploi peut avoir des répercussions sur les déductions fiscales.',
        en: 'Specify your spouse\'s employment income. Employment income may have implications for tax deductions.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  employmentIncome: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Montant réel de dividende déterminé reçu',
        en: 'Actual amount of eligible dividend received',
      },
      tooltip: {
        fr: 'Indiquez le montant réel de dividende déterminé reçu par votre conjoint. Ce montant est utilisé pour les calculs fiscaux.',
        en: 'Specify the actual amount of eligible dividends received by your spouse. This amount is used for tax calculations.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  realEligibleDividend: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Montant réel de dividende ordinaire reçu',
        en: 'Actual amount of common dividend received',
      },
      tooltip: {
        fr: 'Indiquez le montant réel de dividende ordinaire reçu par votre conjoint. Ce montant est utilisé pour les calculs fiscaux.',
        en: 'Specify the actual amount of common dividends received by your spouse. This amount is used for tax calculations.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  realNonEligibleDividend: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Montant de revenu d\'entreprise',
        en: 'Business income',
      },
      tooltip: {
        fr: 'Indiquez le montant du revenu d\'entreprise de votre conjoint. Le revenu d\'entreprise peut avoir des répercussions sur les déductions fiscales.',
        en: 'Specify your spouse\'s business income. Business income may have implications for tax deductions.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  businessIncome: number = 0;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Autres revenus à inclure à la ligne 13000',
        en: 'Other income to include on line 13000',
      },
      tooltip: {
        fr: 'La ligne 13000 (Autres revenus) est une catégorie fourre-tout pour divers types de revenus : Cette ligne est utilisée pour déclarer tout revenu imposable qui ne rentre pas dans les autres catégories de revenus spécifiques répertoriées sur le formulaire de déclaration de revenus.\n' +
          'Exemples de revenus déclarés à la ligne 13000 :\n' +
          ' - Indemnités de départ\n' +
          ' - Paiements forfaitaires\n' +
          ' - Avantages décès\n' +
          ' - Paiements d\'un Régime enregistré d\'épargne-études (REEE)\n' +
          ' - Autres revenus non déclarés ailleurs',
        en: 'Line 13000 (Other Income) is a catch-all for various income types: This line is used to report any taxable income that doesn\'t fit into the other specific income categories listed on the tax return form.\n' +
          'Examples of income reported on line 13000:\n' +
          ' - Retiring allowances\n' +
          ' - Lump-sum payments\n' +
          ' - Death benefits\n' +
          ' - Payments from a Registered Education Savings Plan (RESP)\n' +
          ' - Other income not reported elsewhere',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  othersIncomesLine13000: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce que vous êtes admissible à l\'allocation canadienne pour les travailleurs?',
        en: 'Are you eligible for the Canada workers benefit?',
      },
      tooltip: {
        fr: 'Indiquez si votre conjoint est admissible à l\'allocation canadienne pour les travailleurs. L\'admissibilité à cette allocation peut avoir des répercussions sur les impôts.',
        en: 'Specify whether your spouse is eligible for the Canada workers benefit. Eligibility for this benefit may have tax implications.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  isEligibleToCWB: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce que le salarié assujetti à l\'assurance-emploi?',
        en: 'Is the employee subject to employment insurance?',
      },
      tooltip: {
        fr: 'Indiquez si le salarié est assujetti à l\'assurance-emploi. Cela peut avoir un impact sur les déductions fiscales.',
        en: 'Specify whether the employee is subject to employment insurance. This may have implications for tax deductions.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  isEmployeeSubjectToEI: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Salaire pour le calcul de l\'assurance-emploi',
        en: 'Salary for employment insurance calculus',
      },
      tooltip: {
        fr: '',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  employmentIncomeForEI: number = 0;
}

